/**
 * VaultWorkspace — owns every vault-dependent read/write hook for a resolved
 * vault and shares them with whichever tab is active. Mounting both tabs off a
 * single hook set means switching tabs is instant and never refetches.
 */

import { useCallback, useEffect, useMemo } from "react";
import { getChainConfig } from "@thema/sdk/ui";
import {
  useWallet,
  useVaultAssetMetadata,
  useWalletCapStatus,
  useVaultData,
  useVaultShareDecimals,
  useVaultWrite,
} from "@thema/ui";
import type {
  VaultConfig,
  VaultAssetMetadata,
  WalletCapStatus,
  VaultState,
  UserVaultPosition,
} from "@thema/ui";
import { POLL_INTERVAL_MS } from "../console-config";
import type { ConsoleTab } from "./Dashboard";
import MyAnalytics from "../pages/MyAnalytics";
import VaultControl from "../pages/VaultControl";

export interface WorkspaceData {
  vault: VaultConfig;
  assetMeta: VaultAssetMetadata | null;
  assetMetaLoading: boolean;
  assetMetaError: Error | null;
  assetSymbol: string;
  assetDecimals: number;
  accountingSymbol: string;
  accountingDecimals: number;
  shareDecimals: number;
  assetAddress: `0x${string}` | undefined;
  capStatus: WalletCapStatus | null;
  capLoading: boolean;
  capError: Error | null;
  vaultState: VaultState | null;
  userPosition: UserVaultPosition | null;
  vaultLoading: boolean;
  writes: ReturnType<typeof useVaultWrite>;
  connected: `0x${string}` | undefined;
  isOnCorrectChain: boolean;
  explorerBase: string | undefined;
  refetchAll: () => Promise<void>;
}

export default function VaultWorkspace({
  vault,
  tab,
}: {
  vault: VaultConfig;
  tab: ConsoleTab;
}) {
  const { address, isConnected, chainId } = useWallet();
  const connected = (address as `0x${string}` | undefined) ?? undefined;

  const vaultChainIdNum = parseInt(vault.chainId, 10);
  const isOnCorrectChain = chainId === vaultChainIdNum;

  // Source the deposit asset & accounting token straight from chain — off-chain
  // registry metadata has historically mislabeled these.
  const {
    metadata: assetMeta,
    isLoading: assetMetaLoading,
    error: assetMetaError,
  } = useVaultAssetMetadata({ vault });

  const assetSymbol = assetMeta?.asset.symbol ?? "";
  const assetDecimals = assetMeta?.asset.decimals ?? 0;
  const accountingSymbol = assetMeta?.accountingBase.symbol ?? "";
  const accountingDecimals = assetMeta?.accountingBase.decimals ?? 0;
  const assetAddress = assetMeta?.asset.address;
  const capDecimals = assetMeta ? { assetDecimals, accountingDecimals } : undefined;

  const {
    status: capStatus,
    isLoading: capLoading,
    error: capError,
    refetch: refetchCap,
  } = useWalletCapStatus({ vault, wallet: connected, decimals: capDecimals });

  const {
    vaultState,
    userPosition,
    isLoading: vaultLoading,
    refetch: refetchVault,
  } = useVaultData({
    vault: assetMeta ? { ...vault, assetSymbol, assetDecimals } : vault,
    userAddress: connected,
  });

  // Real share decimals from chain (vault.decimals()). For ThemaLPVault this is
  // assetDecimals + 6, but reading it directly avoids silently inflating redeem
  // amounts if the offset ever changes.
  const shareDecimalsFromChain = useVaultShareDecimals(vault);
  const shareDecimals = shareDecimalsFromChain ?? assetDecimals + 6;

  const writes = useVaultWrite({
    vaultAddress: vault.vaultAddress,
    chainId: vault.chainId,
    assetDecimals,
    shareDecimals,
  });

  const explorerBase = useMemo(() => {
    try {
      const { chain } = getChainConfig(vault.chainId);
      return chain.blockExplorers?.default?.url?.replace(/\/$/, "");
    } catch {
      return undefined;
    }
  }, [vault.chainId]);

  const refetchAll = useCallback(async () => {
    await Promise.all([refetchVault(), refetchCap()]);
  }, [refetchVault, refetchCap]);

  // Keep on-chain reads live without a manual reload — drives the position and
  // analytics cells so they reflect a fresh deposit/redeem within one poll.
  useEffect(() => {
    if (!isConnected) return;
    const id = setInterval(() => {
      void refetchAll();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isConnected, refetchAll]);

  const ws: WorkspaceData = {
    vault,
    assetMeta,
    assetMetaLoading,
    assetMetaError,
    assetSymbol,
    assetDecimals,
    accountingSymbol,
    accountingDecimals,
    shareDecimals,
    assetAddress,
    capStatus,
    capLoading,
    capError,
    vaultState,
    userPosition,
    vaultLoading,
    writes,
    connected,
    isOnCorrectChain,
    explorerBase,
    refetchAll,
  };

  // The "analytics" tab is the DEPOSITOR's own analytics (bound wallet, data-API
  // sourced). The wallet is already guaranteed connected + matched by the global
  // WalletGate around the whole console, so no per-tab gate is needed here.
  return tab === "analytics" ? <MyAnalytics /> : <VaultControl ws={ws} />;
}
